import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  ChartPieIcon,
  ChartBarIcon,
  ClipboardIcon,
  Cog6ToothIcon,
  BellIcon,
  PhoneIcon
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import logo from "../images/did_logo1.png";
import BuyerDashborad from "../components/BuyersComponents/BuyerDashborad";
import ManageTarget from "../components/BuyersComponents/ManageTarget";
import LiveCall from "../components/BuyersComponents/LiveCall";
import BuyersReportTable from "../components/BuyersComponents/BuyersReportTable";
import axios from 'axios';

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function BuyerAdminLayout() {
  const [buyerData, setBuyerData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // Define navigation items
  const defaultNavigation = [
    // {
    //   name: "Dashboard",
    //   component: <BuyerDashborad />,
    //   icon: ChartPieIcon,
    //   current: true,
    // },

    {
      name: "Report",
      component: <BuyersReportTable />,
      icon: ChartBarIcon,
      current: false,
    },

    {
      name: "Live Call",
      component: <LiveCall />,
      icon: PhoneIcon,
      current: false,
    },
    {
      name: "Manage Target",
      component: <ManageTarget />,
      icon: ClipboardIcon,
      current: false,
    },
  ];

  const [navigation, setNavigation] = useState(defaultNavigation);
  const [currentComponent, setCurrentComponent] = useState(defaultNavigation[0].component);

  const [currentDateTime, setCurrentDateTime] = useState(
    new Date()
      .toLocaleString("en-GB", {
        timeZone: "GMT",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+:\d+)/, "$3-$2-$1 $4 UTC")
  );

  useEffect(() => {
    // Check token expiration
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('buyerToken');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expirationTime = payload.exp * 1000; // Convert to milliseconds
          
          if (Date.now() >= expirationTime) {
            // Token has expired
            localStorage.removeItem('buyerToken');
            localStorage.removeItem('buyerData');
            navigate('/buyer/login');
          }
        } catch (error) {
          console.error('Error checking token:', error);
          localStorage.removeItem('buyerToken');
          localStorage.removeItem('buyerData');
          navigate('/buyer/login');
        }
      }
    };

    // Check token on component mount
    checkTokenExpiration();

    // Set up interval to check token expiration
    const intervalId = setInterval(checkTokenExpiration, 60000); // Check every minute

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [navigate]);

  // Add axios interceptor for handling 401/403 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem('buyerToken');
          localStorage.removeItem('buyerData');
          navigate('/buyer/login');
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('buyerToken');
    const data = localStorage.getItem('buyerData');
    if (!token || !data) {
      navigate('/buyer/login');
      return;
    }
    setBuyerData(JSON.parse(data));

    // Update time every second
    const timer = setInterval(() => {
      setCurrentDateTime(
        new Date()
          .toLocaleString("en-GB", {
            timeZone: "GMT",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })
          .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+:\d+)/, "$3-$2-$1 $4 UTC")
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('buyerToken');
    localStorage.removeItem('buyerData');
    navigate('/buyer/login');
  };

  // Add error boundary for currentComponent rendering
  const renderCurrentComponent = () => {
    try {
      return currentComponent || defaultNavigation[0].component;
    } catch (error) {
      console.error('Error rendering component:', error);
      return <BuyerDashborad />; // Fallback to Dashboard component
    }
  };

  if (!buyerData) return null;

  return (
    <div className="mt-[-64px]">
      {/* Mobile sidebar */}
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon className="size-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>

            {/* Mobile sidebar content */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
                <Link to="/">
                  <img className="h-8 w-auto" src={logo} alt="DiDRoute" />
                </Link>
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <a
                            onClick={() => {
                              setCurrentComponent(item.component);
                              setNavigation(navigation.map(nav => ({
                                ...nav,
                                current: nav.name === item.name
                              })));
                            }}
                            className={classNames(
                              item.current
                                ? "bg-gray-200 text-black"
                                : "text-gray-900",
                              "group flex gap-x-3 rounded-md p-2 text-sm font-medium cursor-pointer"
                            )}
                          >
                            <item.icon className="size-6 shrink-0" aria-hidden="true" />
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-48 lg:flex-col border-r border-gray-200">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto custom-scrollbar bg-white px-4 pb-4">
          <Link to="/" className="flex h-16 shrink-0 items-center">
            <img className="h-12 w-80" src={logo} alt="DiDRoute" />
          </Link>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2">
                  <div className="text-gray-900 flex items-center font-semibold mb-2 px-2 pb-2 rounded-md shadow-sm">
                    <span className="text-xs">{currentDateTime}</span>
                  </div>

                  {navigation.map((item) => (
                    <li key={item.name}>
                      <a
                        onClick={() => {
                          setCurrentComponent(item.component);
                          setNavigation(navigation.map(nav => ({
                            ...nav,
                            current: nav.name === item.name
                          })));
                        }}
                        className={classNames(
                          item.current
                            ? "bg-gray-200 text-black"
                            : "text-gray-900",
                          "group flex gap-x-3 rounded-md p-2 text-xs/5 font-medium cursor-pointer"
                        )}
                      >
                        <item.icon className="size-4 shrink-0" aria-hidden="true" />
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="mt-auto">
                <a
                  href="#"
                  className="group -mx-2 flex gap-x-3 rounded-md p-2 text-xs/5 font-semibold text-black hover:bg-gray-300 hover:text-black"
                >
                  <Cog6ToothIcon className="size-5 shrink-0" aria-hidden="true" />
                  Help Center
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-48">
        <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="size-6" aria-hidden="true" />
          </button>

          <div className="h-6 w-px bg-gray-900/10 lg:hidden" aria-hidden="true" />

          <div className="flex flex-1 gap-x-3 self-stretch lg:gap-x-3">
            <form className="flex flex-1" />
            <div className="flex items-center gap-x-2 lg:gap-x-4">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-500"
              >
                <span className="sr-only">View notifications</span>
                <BellIcon className="size-5" aria-hidden="true" />
              </button>
              <div
                className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
                aria-hidden="true"
              />
              <div className="flex items-center">
                <span className="mr-4 text-sm text-gray-700">{buyerData?.email}</span>
                <button
                  onClick={handleLogout}
                  className="bg-red-500 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-red-600"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        <main className="py-6 mt-[64px]">
          <div className="px-2 sm:px-2 lg:px-4">
            {renderCurrentComponent()}
          </div>
        </main>
      </div>
    </div>
  );
}
